import { memo, useCallback, useState } from 'react';

import type { KcContext } from 'keycloakify/login/KcContext';

import { useGetMsg } from 'src/shared/utils/kc-messages.js';

import { ErrorIcon } from './error-icon.js';
import { cnError } from './error.constants.js';
import { InfoIcon } from './info-icon.js';

import './error.css';

type KcActionContext = {
    actionUri?: string;
    pageRedirectUri?: string;
    skipLink?: boolean;
};

export interface ErrorProps {
    kcContext: (KcContext.Error | KcContext.Info | KcContext.LoginPageExpired) &
        KcActionContext & { requiredActions?: string[] };
}

export const Error = memo<ErrorProps>(({ kcContext }) => {
    const { pageId, actionUri = '', requiredActions } = kcContext;
    const [disabledButton, setDisabledButton] = useState(false);

    const kcMessage = kcContext.message?.summary;

    const { msgStr } = useGetMsg(kcContext);

    const isInfoPage = pageId === 'info.ftl';
    const isLoginPageExpiredPage = pageId === 'login-page-expired.ftl';

    const SvgImage = isInfoPage || isLoginPageExpiredPage ? InfoIcon : ErrorIcon;
    const title = isInfoPage
        ? kcMessage
        : isLoginPageExpiredPage
          ? msgStr('expiredPageTitle')
          : msgStr('errorPageTitle');
    const description = isInfoPage ? undefined : isLoginPageExpiredPage ? msgStr('expiredPageDescription') : kcMessage;

    const hasAction = Boolean(kcContext?.actionUri);
    const url = hasAction ? kcContext?.actionUri : msgStr('homePage');

    const redirectUrl = kcContext?.pageRedirectUri;

    const skipBackToMainButton = kcContext?.skipLink;

    const onClickButton = useCallback(() => {
        setDisabledButton(true);
    }, []);

    if (redirectUrl) {
        window.location.href = redirectUrl;
        return null;
    }

    if (actionUri && requiredActions && requiredActions.length) {
        if (requiredActions[0] === 'FAST_REGISTRATION_PASSWORD') {
            window.location.href = actionUri;

            return null;
        }
    }

    return (
        <div className={cnError('content')}>
            <SvgImage className={cnError('image')} />
            <div className={cnError('title')}>{title}</div>
            {description && <div className={cnError('message')}>{description}</div>}
            {!skipBackToMainButton && (
                <div className={cnError('button-container')} onClick={onClickButton}>
                    <a className={cnError('button', { disabled: disabledButton })} href={url}>
                        {hasAction ? msgStr('genericAction') : msgStr('backToTheMain')}
                    </a>
                </div>
            )}
        </div>
    );
});
